import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { logisticsApi } from 'api/logistics';
import { crossDockPurchaseOrderQueryKeys } from 'components/logistics/components/crossDockPurchaseOrders/useCrossDockPurchaseOrdersSearch';

export function useCrossDockPurchaseOrderActions() {
    const queryClient = useQueryClient();
    const pendingDownloadIds = useRef(new Set());
    const pendingCancelIds = useRef(new Set());
    const [downloadingIds, setDownloadingIds] = useState(new Set());
    const [cancellingIds, setCancellingIds] = useState(new Set());

    const downloadPdf = useCallback(async (row) => {
        const id = row?.id;
        if (!id || pendingDownloadIds.current.has(id)) return;

        pendingDownloadIds.current.add(id);
        setDownloadingIds(new Set(pendingDownloadIds.current));
        try {
            const response = await logisticsApi.downloadCrossDockPurchaseOrderPDF(id);
            if (!response?.status) {
                toast.error(response?.msg || 'Failed to download PDF.');
            }
        } catch (error) {
            toast.error(error?.message || 'Failed to download PDF.');
        } finally {
            pendingDownloadIds.current.delete(id);
            setDownloadingIds(new Set(pendingDownloadIds.current));
        }
    }, []);

    const cancelPurchaseOrder = useCallback(
        async (row) => {
            const id = row?.id;
            if (!id || pendingCancelIds.current.has(id)) return false;

            pendingCancelIds.current.add(id);
            setCancellingIds(new Set(pendingCancelIds.current));
            try {
                const response = await logisticsApi.cancelCrossDockPurchaseOrder(id);
                if (!response?.status) {
                    toast.error(response?.msg || 'Failed to cancel cross dock purchase order.');
                    return false;
                }
                await queryClient.invalidateQueries({ queryKey: crossDockPurchaseOrderQueryKeys.all });
                toast.success('Cross dock purchase order cancelled');
                return true;
            } catch (error) {
                toast.error(error?.message || 'Failed to cancel cross dock purchase order.');
                return false;
            } finally {
                pendingCancelIds.current.delete(id);
                setCancellingIds(new Set(pendingCancelIds.current));
            }
        },
        [queryClient],
    );

    return { cancelPurchaseOrder, cancellingIds, downloadPdf, downloadingIds };
}
